// Един ред на „Целия списък" (role="option") — ЕДИН ред на продукт, с еднаква
// височина в рамките на подредбата (прозорецът смята по нея):
//   columns — решетка Продукт | Ед. | Наличност | Мин. | [Количество] | Цена | [Сума];
//   meta — заварения ред: името + метатекстът приглушено до него;
//   narrow — телефон: името (+ вторичното) и вдясно наличност · цена.
// Вторичното (втори текст — „−1,000 продадени без наличност", „Рецепта";
// иначе групата, когато се гледат всички групи) стои приглушено до името.
// С въвеждане (`entry`) отметнатият ред носи полетата (CatalogEntryCells), а
// кликът по реда го отмята и слага курсора в количеството; кликът по
// квадратчето само отмята/маха отметката.
// Вече добавеният ред носи значка „в документа"; ако не може да се отметне
// (по подразбиране), на мястото на квадратчето стои зелена отметка.
// memo: при ↑/↓ се прерисуват само двата засегнати реда, не 3 хил.
import { memo, type ReactNode } from "react";
import { CheckOutlined } from "@ant-design/icons";
import { CheckBox } from "../filters/CheckBox";
import { highlightWords } from "../highlight";
import type { CatalogColumns, CatalogItem, CatalogStockTone } from "./catalogPickerModel";
import type { CatalogEntryConfig, CatalogEntryValue } from "./catalogEntryModel";
import {
  CatalogLineSum,
  CatalogPriceField,
  CatalogQtyField,
  type CatalogFieldHandlers,
} from "./CatalogEntryCells";
import {
  CATALOG_CHECK_CELL_CLASS,
  CATALOG_MINIMUM_CELL_CLASS,
  CATALOG_NARROW_GUTTER_CLASS,
  CATALOG_NARROW_PRICE_CLASS,
  CATALOG_NARROW_STOCK_CLASS,
  CATALOG_PRICE_CELL_CLASS,
  CATALOG_QTY_CELL_CLASS,
  CATALOG_ROW_GUTTER_CLASS,
  CATALOG_ROW_HEIGHT_CLASS,
  CATALOG_STOCK_CELL_CLASS,
  CATALOG_SUM_CELL_CLASS,
  CATALOG_UNIT_CELL_CLASS,
  type CatalogRowVariant,
} from "./catalogPickerLayout";

// Празно — червеникаво, малко — оранжево, има — обикновено мастило
const STOCK_TONE_CLASS: Record<CatalogStockTone, string> = {
  none: "text-danger-ink",
  low: "text-warn-ink",
  ok: "text-ink-2",
};

type Props = Partial<CatalogFieldHandlers> & {
  item: CatalogItem;
  index: number;
  rowCount: number;
  optionId: string;
  variant: CatalogRowVariant;
  columns: CatalogColumns;
  words: string[];
  picked: boolean;
  active: boolean;
  selectable: boolean;
  inDocumentLabel: string;
  // групата до името, когато се гледат всички групи (иначе null)
  groupLabel: string | null;
  onToggle: (item: CatalogItem) => void;
  // само на десктоп: мишката мести маркера (на телефон няма hover)
  onHover?: (index: number) => void;
  // ─── въвеждането в реда ───
  entry?: CatalogEntryConfig;
  // написаното — само за отметнат ред (memo: неотметнатите не се прерисуват)
  entryValue?: CatalogEntryValue;
  // клик по реда: отмята и фокусира количеството
  onEdit?: (item: CatalogItem, index: number) => void;
  // aria-label на полетата
  fieldLabels?: { qty: string; price: string };
};

// В тесен списък (контейнерът под 1000px) — само отметката; надписът е в title
const AddedBadge = ({ label }: { label: string }) => (
  <span
    title={label}
    className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-ok-tint px-1.5 py-px text-[11px] font-medium leading-[1.4] text-ok-deep @min-[1000px]:px-2"
  >
    <CheckOutlined aria-label={label} className="text-[9px]" />
    <span aria-hidden className="hidden @min-[1000px]:inline">
      {label}
    </span>
  </span>
);

const StockValue = ({ item, withUnit }: { item: CatalogItem; withUnit: boolean }) =>
  item.stock ? (
    <span className={`font-sheet tabular-nums ${STOCK_TONE_CLASS[item.stockTone ?? "ok"]}`}>
      {item.stock}
      {withUnit && item.unit ? <span className="font-sans text-muted"> {item.unit}</span> : null}
    </span>
  ) : null;

export const CatalogPickerRow = memo(function CatalogPickerRow({
  item,
  index,
  rowCount,
  optionId,
  variant,
  columns,
  words,
  picked,
  active,
  selectable,
  inDocumentLabel,
  groupLabel,
  onToggle,
  onHover,
  entry,
  entryValue,
  onEdit,
  fieldLabels,
  onFieldChange,
  onFieldKeyDown,
  onFieldFocus,
}: Props) {
  const isNarrow = variant === "narrow";
  const name = highlightWords(item.name, words);
  // вторичното: вторият текст; иначе метатекстът (заварен ред); иначе групата
  const secondaryText = item.sub ?? (variant === "meta" ? item.meta || null : null) ?? groupLabel;
  const secondary = secondaryText ? highlightWords(secondaryText, item.sub ? words : []) : null;
  const badge = item.added ? <AddedBadge label={inDocumentLabel} /> : null;
  const editing = !!entry && picked && selectable;
  const withPrice = columns.price || entry?.kind === "qtyPrice";
  const fieldProps =
    editing && onFieldChange && onFieldKeyDown && onFieldFocus
      ? { item, index, optionId, value: entryValue, isNarrow, onFieldChange, onFieldKeyDown, onFieldFocus }
      : null;

  const nameBlock = (
    <span className="flex min-w-0 flex-1 items-baseline gap-2">
      <span
        title={item.name}
        className={`min-w-0 shrink truncate font-medium ${isNarrow ? "text-[14.5px]" : "text-[13.5px]"} ${
          selectable ? "text-ink" : "text-soft"
        }`}
      >
        {name}
      </span>
      {/* вторичното се свива първо — името има предимство */}
      {secondary && (
        <span title={secondaryText ?? undefined} className="min-w-0 shrink-[1000] truncate text-[12px] text-muted">
          {secondary}
        </span>
      )}
      {badge}
    </span>
  );

  let content: ReactNode;
  if (isNarrow) {
    content = (
      <>
        {nameBlock}
        {fieldProps ? (
          <>
            <span className="flex shrink-0 items-center gap-1">
              <CatalogQtyField {...fieldProps} label={fieldLabels?.qty ?? ""} />
            </span>
            {entry?.kind === "qtyPrice" && <CatalogPriceField {...fieldProps} label={fieldLabels?.price ?? ""} />}
          </>
        ) : (
          <>
            {item.stock && (
              <span className={`${CATALOG_NARROW_STOCK_CLASS} text-[13px]`}>
                <StockValue item={item} withUnit />
              </span>
            )}
            {columns.structured && !item.stock && item.unit && (
              <span className="shrink-0 text-[12.5px] text-muted">{item.unit}</span>
            )}
            {item.price && (
              <span className={`${CATALOG_NARROW_PRICE_CLASS} font-sheet text-[12.5px] tabular-nums text-muted`}>
                {item.price}
              </span>
            )}
          </>
        )}
      </>
    );
  } else if (variant === "meta") {
    content = nameBlock;
  } else {
    content = (
      <>
        {nameBlock}
        {columns.unit && <span className={`${CATALOG_UNIT_CELL_CLASS} text-[12.5px] text-body`}>{item.unit}</span>}
        {columns.stock && (
          <span className={`${CATALOG_STOCK_CELL_CLASS} text-[13px]`}>
            <StockValue item={item} withUnit={false} />
          </span>
        )}
        {columns.minimum && (
          <span className={`${CATALOG_MINIMUM_CELL_CLASS} font-sheet text-[12.5px] tabular-nums text-muted`}>
            {item.minimum}
          </span>
        )}
        {entry && (
          <span className={CATALOG_QTY_CELL_CLASS}>
            {fieldProps ? (
              <CatalogQtyField {...fieldProps} label={fieldLabels?.qty ?? ""} />
            ) : (
              // стойността на реда в листа — приглушено, без поле
              <span className="truncate font-sheet text-[12.5px] tabular-nums text-faint">{item.filledValue}</span>
            )}
          </span>
        )}
        {withPrice && (
          <span className={`${CATALOG_PRICE_CELL_CLASS} font-sheet text-[12.5px] tabular-nums text-ink-2`}>
            {fieldProps && entry?.kind === "qtyPrice" ? (
              <CatalogPriceField {...fieldProps} label={fieldLabels?.price ?? ""} />
            ) : (
              item.price
            )}
          </span>
        )}
        {entry?.kind === "qtyPrice" && (
          <span className={`${CATALOG_SUM_CELL_CLASS} text-[12.5px]`}>
            {editing ? <CatalogLineSum item={item} value={entryValue} /> : null}
          </span>
        )}
      </>
    );
  }

  const background = picked ? (active ? "bg-brand-tint2" : "bg-brand-tint") : active ? "bg-fill" : "bg-transparent";
  return (
    <div
      id={optionId}
      role="option"
      aria-selected={picked}
      aria-disabled={!selectable || undefined}
      aria-posinset={index + 1}
      aria-setsize={rowCount}
      data-catalog-index={index}
      // Десктоп: кликът не взема фокуса от филтъра (там живее клавиатурата).
      // Телефон: нека полето загуби фокус — клавиатурата се прибира.
      onMouseDown={onHover ? (event) => event.preventDefault() : undefined}
      onMouseMove={onHover ? () => onHover(index) : undefined}
      onClick={() => (onEdit ? onEdit(item, index) : onToggle(item))}
      className={`flex w-full items-center border-b border-shade-1 text-left ${
        isNarrow ? CATALOG_NARROW_GUTTER_CLASS : CATALOG_ROW_GUTTER_CLASS
      } ${CATALOG_ROW_HEIGHT_CLASS[variant]} ${background} ${active ? "shadow-[inset_3px_0_0_var(--color-brand)]" : ""} ${
        selectable ? "cursor-pointer" : "cursor-default"
      }`}
    >
      <span
        className={CATALOG_CHECK_CELL_CLASS}
        // с въвеждане кликът по реда отива в полето — квадратчето само отмята/маха
        onClick={
          onEdit
            ? (event) => {
                event.stopPropagation();
                onToggle(item);
              }
            : undefined
        }
      >
        {selectable ? <CheckBox checked={picked} /> : <CheckOutlined aria-hidden className="text-[13px] text-ok-deep" />}
      </span>
      {content}
    </div>
  );
});

export default CatalogPickerRow;
